import { useState } from '#imports'
import { type MaybeRefOrGetter, computed, toValue, watch } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { useUtmSource } from '~/scopes/product-core/composables/useUtmSource/useUtmSource'

import { CONFIGURED_PARTNER_NAMES } from './telcoPlanComparison.config'
import type { PartnerNames, TelcoPickerOption } from './types'

/**
 * Smartphone category id (backend "categoryId"). Declared locally to keep the
 * `product-core` scope isolated from the `product` scope.
 */
const SMARTPHONE_CATEGORY_ID = '2'

/** Sentinel meaning "no telco plan selected". `selectedPartner === NO_PLAN`. */
export const NO_PLAN = null

/**
 * Encapsulates the PDP telco-picker eligibility and offer sourcing.
 *
 * The picker REPLACES the legacy single-partner popup only when the offers
 * payload carries at least TWO *configured* partner offers. Eligibility
 * (all must hold):
 *  - market is US, AND
 *  - the product is an unlocked smartphone (`carrier.locked === false`,
 *    smartphone category), AND
 *  - the visit is non-referral (`!useUtmSource()`), AND
 *  - the `experiment.ppTelcoComparison` flag resolves to `withPicker`, AND
 *  - there are at least TWO partner offers whose `partnerName` is in
 *    {@link CONFIGURED_PARTNER_NAMES} (i.e. has a comparison-drawer entry).
 *
 * Offers are mapped from the product payload's
 * `includedServiceOffers.partnerPromoCodes` (the existing, non-blocking source —
 * no extra fetch). Only partners with a full comparison config
 * ({@link TELCO_PLAN_COMPARISONS}) are surfaced: config-less partner offers
 * (GOOGLE_FI, BESTPHONEPLANS, RAKUTEN, ...) are silently discarded before
 * eligibility is evaluated, so the picker never shows a partner it cannot
 * compare. Each option's `offerId` always comes from the backend
 * (`offerId ?? id`), never from user input.
 *
 * The composable is persistence-agnostic: it owns the in-memory selection and
 * exposes `selectPlan`. Cart assignment is handled in `useAddToCart` via the
 * `telcoSelection` descriptor (`telcoCartSelection` computed).
 *
 * @param product - The current product (ref/getter/value). Provided by the page.
 */
export function useTelcoPicker(
  product: MaybeRefOrGetter<GetProductResponse | null | undefined>,
) {
  const marketplace = useMarketplace()
  const experiments = useExperiments()
  // Resolve referral state at composable root (not inside a computed): the value
  // is captured from the URL/session cookie at setup and is stable for the PDP
  // visit. (custom-rule: no-composables-in-computed)
  const isReferral = Boolean(useUtmSource())

  const isUS = computed(
    () => marketplace.market.countryCode === MarketCountryCode.US,
  )

  const isUnlockedSmartphone = computed(() => {
    const value = toValue(product)
    if (!value) return false

    return (
      value.tracking?.categoryId === SMARTPHONE_CATEGORY_ID &&
      value.carrier?.locked === false
    )
  })

  const isFlagOn = computed(
    () => experiments['experiment.ppTelcoComparison'] === 'withPicker',
  )

  /**
   * Configured partner offers from the payload, mapped to picker options.
   * Non-configured partners (no entry in {@link TELCO_PLAN_COMPARISONS}) are
   * filtered out BEFORE mapping — the picker only surfaces partners it can
   * fully compare. Computed independently of eligibility to avoid a circular
   * dependency (eligibility depends on the configured offer count, so it
   * cannot read `options`).
   */
  const partnerOffers = computed<TelcoPickerOption[]>(() => {
    const partnerPromoCodes =
      toValue(product)?.includedServiceOffers?.partnerPromoCodes ?? []

    return partnerPromoCodes
      .filter((offer) => CONFIGURED_PARTNER_NAMES.has(offer.partnerName))
      .map((offer) => ({
        // offerId is backend-sourced only. Never user input.
        offerId: offer.offerId ?? offer.id,
        partnerName: offer.partnerName,
      }))
  })

  const isPickerEligible = computed(
    () =>
      isUS.value &&
      isUnlockedSmartphone.value &&
      !isReferral &&
      isFlagOn.value &&
      partnerOffers.value.length >= 2,
  )

  const options = computed<TelcoPickerOption[]>(() =>
    isPickerEligible.value ? partnerOffers.value : [],
  )

  const selectedPartner = useState<PartnerNames | null>(
    'telco-picker-selected-partner',
    () => NO_PLAN,
  )

  /**
   * Shared latch: once ANY composable instance on the page sees the picker as
   * eligible, this stays `true` for the entire PDP visit. This is the fix for
   * the B1/B2 timing issue: ContainerAddToCart may call `useTelcoPicker` before
   * its own product ref is populated (null), making `isPickerEligible` false for
   * that instance. Without a shared latch, `telcoCartSelection.isPickerActive`
   * would be false for that container → legacy fallback would wrongly auto-assign
   * Visible even when the user selected "No plan" or NOBLE via the picker.
   */
  const sharedIsActive = useState<boolean>(
    'telco-picker-is-active',
    () => false,
  )

  /**
   * The productId for which the shared latch and selection are currently valid.
   * Set by the first instance that confirms eligibility for a productId, or by
   * the reset watcher when navigating to a genuinely non-eligible product.
   * Used to detect cross-product navigation vs. same-product instance variations.
   */
  const sharedActiveProductId = useState<string | null>(
    'telco-picker-active-product-id',
    () => null,
  )

  // Latch sharedIsActive to true as soon as any instance's isPickerEligible goes
  // true. It must NOT reset on the transient empty/null payload during a
  // same-product funnel-variant refetch (that transient was the original
  // disappear-on-click bug, and resetting here would re-open the legacy path
  // mid-visit — reintroducing B1/B2). The reset therefore keys on PRODUCT
  // IDENTITY (`productId`), not on raw eligibility: a genuinely loaded product
  // (productId defined) that is non-eligible means we navigated to a different,
  // non-telco/non-picker PDP and the picker experience no longer applies.
  watch(
    isPickerEligible,
    (eligible) => {
      if (eligible) {
        sharedIsActive.value = true
        // Record the productId for which the latch is valid, so the reset watcher
        // can distinguish "second instance on same eligible PDP" from "navigation
        // to a non-eligible PDP".
        const pid = toValue(product)?.productId
        if (pid !== undefined) {
          sharedActiveProductId.value = pid
        } else if (import.meta.dev) {
          // Invariant: eligibility implies a loaded product (isUnlockedSmartphone
          // is false for a null product). If this ever fires, the eligibility
          // computation changed and the reset watcher can no longer distinguish a
          // same-product secondary instance from a cross-product navigation —
          // surfacing it here prevents a silent selection-loss regression.
          // eslint-disable-next-line no-console
          console.warn(
            '[useTelcoPicker] picker is eligible but productId is undefined — sharedActiveProductId not recorded; reset guard may misfire.',
          )
        }
      }
    },
    { immediate: true },
  )

  // Reset the latch (and any stale selection) on client-side navigation to a
  // DIFFERENT product that is not picker-eligible — so the control-arm / legacy
  // auto-assign path is reachable again on subsequent PDPs. Gated on a defined
  // `productId` so the transient null payload during a same-product refetch never
  // clears an active picker session.
  //
  // Fix for "selection lost on add-to-cart": neither the latch nor selectedPartner
  // are cleared when the current productId matches the productId for which the
  // latch was confirmed active (`sharedActiveProductId`). Only a genuine cross-
  // product navigation (different productId) or a first landing on a non-eligible
  // PDP (sharedActiveProductId is null, meaning no instance has confirmed eligibility
  // this visit) triggers the reset.
  watch(
    () => toValue(product)?.productId,
    (productId) => {
      if (productId !== undefined && !isPickerEligible.value) {
        // Skip the reset if this productId was already confirmed eligible by another
        // instance on this page visit — we are a secondary/variant instance on the
        // same PDP, not a cross-product navigation.
        if (sharedActiveProductId.value === productId) return

        sharedIsActive.value = false
        selectedPartner.value = NO_PLAN
        sharedActiveProductId.value = null
      }
    },
    // `immediate` is required: `sharedIsActive` lives in `useState` and can
    // already be `true` on first mount (prior-session hydration, bf-cache, or a
    // direct hard-navigation to a non-eligible PDP). Without it the reset would
    // only fire on a SUBSEQUENT productId change, leaving the picker wrongly
    // active on the first non-eligible page load. Mirrors the latch watch above.
    { immediate: true },
  )

  function selectPlan(partnerName: PartnerNames | null) {
    selectedPartner.value = partnerName
  }

  // The selection lives in `useState` (a process-global, product-agnostic slot)
  // so it survives the picker unmount/remount during a funnel-variant refetch.
  // The downside is that the slot also outlives client-side navigation to an
  // unrelated product, leaving a stale selection behind. Drop the selection once
  // the *loaded* product no longer offers the selected partner. Guarded on a
  // non-empty offer list so the TRANSIENT empty payload during a refetch never
  // clears a still-valid selection (that transient unmount was the original
  // disappear-on-click bug).
  watch(partnerOffers, (offers) => {
    if (
      selectedPartner.value !== NO_PLAN &&
      offers.length > 0 &&
      !offers.some((offer) => offer.partnerName === selectedPartner.value)
    ) {
      selectedPartner.value = NO_PLAN
    }
  })

  /**
   * The partner name to pass to `TelcoPartner` as `selectedPartnerName`.
   *
   * - `undefined`  → picker is not eligible; `TelcoPartner` falls back to the
   *                  existing referral/`hasPartner` path (v-else branch). This
   *                  preserves byte-for-byte flag-off behaviour.
   * - `null`       → picker is eligible but no plan is selected; `TelcoPartner`
   *                  renders the picker-driven popup with no partner highlighted
   *                  (or suppresses the popup entirely, per TelcoPartner logic).
   * - string       → the selected partnerName (authoritative closed enum).
   */
  const selectedPartnerName = computed<PartnerNames | null | undefined>(() => {
    if (!isPickerEligible.value) return undefined
    if (selectedPartner.value === NO_PLAN) return null

    // Stale guard: if the selected partner is no longer in the current options
    // (e.g. after navigation to a different product), return null.
    return options.value.some((o) => o.partnerName === selectedPartner.value)
      ? selectedPartner.value
      : null
  })

  /**
   * The full picker option for the currently selected partner, or null.
   * Keyed exclusively on partnerName (the unique closed enum) to avoid
   * collisions when offerId/id values collide across partners.
   */
  const selectedOffer = computed<TelcoPickerOption | null>(() => {
    if (!selectedPartner.value) return null

    return (
      options.value.find((o) => o.partnerName === selectedPartner.value) ?? null
    )
  })

  /**
   * Cart-relevant descriptor for the telco selection. Single source of truth
   * for all three Add-to-Cart triggers (ContainerAddToCart, Proposal, StickyBarBottom).
   *
   * - `isPickerActive` = true when the picker has been eligible on this page visit
   *   (shared via `sharedIsActive` latch so ALL containers agree even when a
   *   container's product ref is still null at add-to-cart time)
   * - `selectedPartnerName` = the buyer's explicit selection, or null (None / not eligible)
   *
   * When `isPickerActive` is false, the legacy single-offer / referral path in
   * `useAddToCart` applies.
   */
  const telcoCartSelection = computed(() => {
    const isActive = sharedIsActive.value || isPickerEligible.value

    return {
      isPickerActive: isActive,
      selectedPartnerName: isActive ? selectedPartner.value : null,
    }
  })

  return {
    isPickerEligible,
    options,
    selectedOffer,
    selectedPartnerName,
    telcoCartSelection,
    selectPlan,
    NO_PLAN,
  }
}
